import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface Return {
  id: string;
  returnNumber: string;
  originalOrder: string;
  customer: string;
  items: number;
  value: number;
  reason: string;
  condition: "new" | "opened" | "damaged" | "defective";
  status: "pending" | "inspecting" | "approved" | "restocked" | "rejected";
  date: string;
  time: string;
  processedBy?: string;
}

const MOCK_RETURNS: Return[] = [
  { id: "1", returnNumber: "RET-2024-001", originalOrder: "ORD-2024-015", customer: "John Doe", items: 1, value: 45.99, reason: "Wrong size", condition: "new", status: "restocked", date: "2024-01-15", time: "10:30 AM", processedBy: "Maria G." },
  { id: "2", returnNumber: "RET-2024-002", originalOrder: "ORD-2024-018", customer: "Jane Smith", items: 2, value: 89.50, reason: "Changed mind", condition: "opened", status: "inspecting", date: "2024-01-15", time: "11:15 AM" },
  { id: "3", returnNumber: "RET-2024-003", originalOrder: "ORD-2024-022", customer: "Mike Wilson", items: 1, value: 25.00, reason: "Defective", condition: "defective", status: "approved", date: "2024-01-15", time: "12:00 PM" },
  { id: "4", returnNumber: "RET-2024-004", originalOrder: "ORD-2024-025", customer: "Sarah Johnson", items: 3, value: 156.75, reason: "Not as described", condition: "new", status: "pending", date: "2024-01-15", time: "02:30 PM" },
  { id: "5", returnNumber: "RET-2024-005", originalOrder: "ORD-2024-030", customer: "Alex Brown", items: 1, value: 35.00, reason: "Wrong color", condition: "new", status: "restocked", date: "2024-01-14", time: "04:45 PM", processedBy: "Carlos R." },
  { id: "6", returnNumber: "RET-2024-006", originalOrder: "ORD-2024-032", customer: "Emily Davis", items: 2, value: 67.00, reason: "Damaged in shipping", condition: "damaged", status: "rejected", date: "2024-01-14", time: "05:20 PM" },
];

export default function ReturnsSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [conditionFilter, setConditionFilter] = useState<string>("all");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const filteredReturns = MOCK_RETURNS.filter((ret) => {
    const matchesSearch = ret.returnNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ret.originalOrder.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ret.customer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || ret.status === statusFilter;
    const matchesCondition = conditionFilter === "all" || ret.condition === conditionFilter;
    return matchesSearch && matchesStatus && matchesCondition;
  });

  const getStatusBadge = (status: Return["status"]) => {
    const styles = {
      pending: "bg-amber-500/20 text-amber-500",
      inspecting: "bg-violet-500/20 text-violet-500",
      approved: "bg-blue-500/20 text-blue-500",
      restocked: "bg-emerald-500/20 text-emerald-500",
      rejected: "bg-red-500/20 text-red-500",
    };
    const labels = {
      pending: t("returns.statusPending"),
      inspecting: t("returns.statusInspecting"),
      approved: t("returns.statusApproved"),
      restocked: t("returns.statusRestocked"),
      rejected: t("returns.statusRejected"),
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getConditionBadge = (condition: Return["condition"]) => {
    const styles = {
      new: "bg-emerald-500/20 text-emerald-500",
      opened: "bg-blue-500/20 text-blue-500",
      damaged: "bg-amber-500/20 text-amber-500",
      defective: "bg-red-500/20 text-red-500",
    };
    const labels = {
      new: t("returns.conditionNew"),
      opened: t("returns.conditionOpened"),
      damaged: t("returns.conditionDamaged"),
      defective: t("returns.conditionDefective"),
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[condition]}`}>
        {labels[condition]}
      </span>
    );
  };

  const pendingReturns = MOCK_RETURNS.filter((r) => r.status === "pending").length;
  const inspectingReturns = MOCK_RETURNS.filter((r) => r.status === "inspecting").length;
  const restockedItems = MOCK_RETURNS.filter((r) => r.status === "restocked").reduce((acc, r) => acc + r.items, 0);
  const totalValue = MOCK_RETURNS.reduce((acc, r) => acc + r.value, 0);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posMenu.returns")}</h1>
          <p className={themeClasses.textSecondary}>{t("returns.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <button className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2">
            <i className="ri-add-line"></i>
            {t("returns.newReturn")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("returns.pendingReturns")}</span>
            <i className="ri-time-line text-amber-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-amber-500`}>{pendingReturns}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("returns.inspecting")}</span>
            <i className="ri-search-eye-line text-violet-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-violet-500`}>{inspectingReturns}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("returns.restockedItems")}</span>
            <i className="ri-inbox-archive-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-emerald-500`}>{restockedItems}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("returns.totalValue")}</span>
            <i className="ri-money-dollar-circle-line text-blue-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>${totalValue.toFixed(2)}</p>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`p-4 border-b ${themeClasses.border} flex flex-col sm:flex-row gap-4`}>
          <div className="flex-1 relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              type="text"
              placeholder={t("returns.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>

          <div className="flex gap-2">
            <select
              value={conditionFilter}
              onChange={(e) => setConditionFilter(e.target.value)}
              className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
            >
              <option value="all">{t("returns.allConditions")}</option>
              <option value="new">{t("returns.conditionNew")}</option>
              <option value="opened">{t("returns.conditionOpened")}</option>
              <option value="damaged">{t("returns.conditionDamaged")}</option>
              <option value="defective">{t("returns.conditionDefective")}</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
            >
              <option value="all">{t("posOrders.allStatuses")}</option>
              <option value="pending">{t("returns.statusPending")}</option>
              <option value="inspecting">{t("returns.statusInspecting")}</option>
              <option value="approved">{t("returns.statusApproved")}</option>
              <option value="restocked">{t("returns.statusRestocked")}</option>
              <option value="rejected">{t("returns.statusRejected")}</option>
            </select>

            <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} flex items-center gap-2`}>
              <i className="ri-calendar-line"></i>
              {t("posOrders.dateRange")}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
              <tr>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("returns.returnNumber")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("returns.originalOrder")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.customer")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.items")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("returns.condition")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("returns.value")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.status")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredReturns.map((ret) => (
                <tr key={ret.id} className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover}`}>
                  <td className="px-4 py-3">
                    <div>
                      <p className={`font-medium ${themeClasses.text}`}>{ret.returnNumber}</p>
                      <p className={`text-xs ${themeClasses.textSecondary}`}>{ret.date} {ret.time}</p>
                    </div>
                  </td>
                  <td className={`px-4 py-3 ${themeClasses.text}`}>
                    <span className="text-blue-500 hover:underline cursor-pointer">{ret.originalOrder}</span>
                  </td>
                  <td className={`px-4 py-3 ${themeClasses.text}`}>{ret.customer}</td>
                  <td className={`px-4 py-3 text-center ${themeClasses.text}`}>{ret.items}</td>
                  <td className="px-4 py-3 text-center">{getConditionBadge(ret.condition)}</td>
                  <td className={`px-4 py-3 text-right font-bold ${themeClasses.text}`}>${ret.value.toFixed(2)}</td>
                  <td className="px-4 py-3 text-center">{getStatusBadge(ret.status)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button aria-label={t("general.viewDetails", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                        <i className="ri-eye-line"></i>
                      </button>
                      {ret.status === "pending" && (
                        <button className="p-2 rounded-lg hover:bg-violet-500/10 text-violet-500">
                          <i className="ri-search-eye-line"></i>
                        </button>
                      )}
                      {ret.status === "approved" && (
                        <button className="p-2 rounded-lg hover:bg-emerald-500/10 text-emerald-500">
                          <i className="ri-inbox-archive-line"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
